<!-- src/components/UsageStats.vue -->
<template>
  <v-container fluid>
    <v-card style="width: 100%">
      <v-card-title class="title-option">Usage Stats</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="12">
            <v-card class="visits-card">
              <v-icon size="48" color="#b7947e">mdi-map-marker-radius</v-icon>
              <h3>{{ totalClicks }}</h3>
              <p>Total Visits</p>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <VideoViewStats
              class="full-card"
              style="height: 500px"
              :videoStats="videoStats"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-card class="pie-card">
              <v-card-title class="title-option"
                >Location Distribution</v-card-title
              >
              <v-card-text>
                <apexchart
                  type="pie"
                  height="400"
                  :options="locationPieOptions"
                  :series="locationPieSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charts Section -->
        <v-row>
          <!-- Clicks Over Time Line Chart -->
          <v-col cols="12" md="6">
            <v-card class="half-card">
              <v-card-title class="title-option">Clicks Over Time</v-card-title>
              <v-card-text>
                <apexchart
                  type="line"
                  height="300"
                  :options="clicksOverTimeOptions"
                  :series="clicksOverTimeSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- User Agent Distribution Bar Chart -->
          <v-col cols="12" md="6">
            <v-card class="half-card">
              <v-card-title class="title-option"
                >User Agent Distribution</v-card-title
              >
              <v-card-text>
                <apexchart
                  type="bar"
                  height="300"
                  :options="userAgentOptions"
                  :series="userAgentSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Geo Chart for Clicks by Location -->
        <v-row>
          <v-col cols="12">
            <v-card class="full-card">
              <v-card-title class="title-option"
                >Clicks by Location</v-card-title
              >
              <v-card-text>
                <GChart
                  :settings="{ packages: ['geochart'] }"
                  type="GeoChart"
                  :data="geoChartData"
                  :options="geoChartOptions"
                  style="height: 800px; width: 100%"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card class="full-card">
              <v-card-title class="title-option"
                >Usage Details
                <v-spacer></v-spacer>
                <v-switch
                  v-model="combineIPs"
                  style="margin-top: 15px"
                  label="combine IP's"
                  inset
                ></v-switch>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="tableHeadersComputed"
                  :items="tableData"
                  :items-per-page="10"
                  class="elevation-1"
                >
                  <!-- Only use the custom time slot when not aggregating -->
                  <template v-if="!combineIPs" v-slot:[`item.time`]="{ item }">
                    <span class="time-table">
                      {{ formatDateTime(item.time).timePart }},
                    </span>
                    <span class="date-table">
                      {{ formatDateTime(item.time).datePart }}
                    </span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { AdminWS } from "@/services/AdminWS";
import VueApexCharts from "vue3-apexcharts";
import { GChart } from "vue-google-charts";
import VideoViewStats from "./VideoViewStats.vue";

export default {
  name: "UsageStats",
  components: {
    apexchart: VueApexCharts,
    GChart,
    VideoViewStats,
  },
  data() {
    return {
      combineIPs: false,
      sortBy: "time",
      sortDesc: true,
      usageStats: [],
      videoStats: [],
      totalClicks: 0,
      // Line chart data for clicks over time
      clicksOverTimeSeries: [],
      clicksOverTimeOptions: {
        chart: {
          type: "line",
          zoom: { enabled: false },
        },
        colors: ["#b7947e"],
        xaxis: {
          type: "datetime",
          title: { text: "Time" },
        },
        yaxis: {
          title: { text: "Clicks" },
        },
      },
      locationPieSeries: [],
      locationPieOptions: {},
      // Bar chart data for user agent distribution
      userAgentSeries: [],
      userAgentOptions: {
        chart: {
          type: "bar",
        },
        colors: ["#b7947e", "#767676"],
        xaxis: {
          title: { text: "User Agent" },
        },
        yaxis: {
          title: { text: "Clicks" },
        },
      },
      // Geo chart data for clicks by location
      geoChartData: [],
      geoChartOptions: {
        region: "world",

        backgroundColor: "#f8f9fa",
        datalessRegionColor: "#dee2e6",
      },
      tableHeaders: [
        { title: "IP", value: "ip", sortable: true },
        { title: "Mobile/Desktop", value: "device", sortable: true },
        { title: "Location", value: "location", sortable: true },
        { title: "Time", value: "time", sortable: true },
      ],
    };
  },
  mounted() {
    this.fetchUsageStats();
  },
  computed: {
    tableData() {
      if (!this.combineIPs) {
        // Default: map each usage stat as before.
        return this.usageStats.map((item) => {
          const device = /Mobi|Android/i.test(item.user_agent)
            ? "Mobile"
            : "Desktop";
          let location = "Unknown";
          try {
            const geo = JSON.parse(item.geo_location);
            location =
              (geo.city && geo.city !== "Unknown" ? geo.city + ", " : "") +
              geo.country;
          } catch (error) {
            location = "Unknown";
          }
          return {
            ip: item.ip_address,
            device: device,
            location: location,
            time: item.request_time,
          };
        });
      } else {
        // Aggregated view: group by IP
        const aggregated = this.usageStats.reduce((acc, item) => {
          const ip = item.ip_address;
          let location = "Unknown";
          try {
            const geo = JSON.parse(item.geo_location);
            location =
              (geo.city && geo.city !== "Unknown" ? geo.city + ", " : "") +
              geo.country;
          } catch (error) {
            location = "Unknown";
          }
          if (!acc[ip]) {
            acc[ip] = {
              ip: ip,
              location: location,
              count: 0,
              firstVisit: item.request_time,
              lastVisit: item.request_time,
            };
          }
          acc[ip].count++;
          if (item.request_time < acc[ip].firstVisit) {
            acc[ip].firstVisit = item.request_time;
          }
          if (item.request_time > acc[ip].lastVisit) {
            acc[ip].lastVisit = item.request_time;
          }
          return acc;
        }, {});
        // Return an array with formatted time strings
        return Object.values(aggregated).map((entry) => ({
          ip: entry.ip,
          location: entry.location,
          count: entry.count,
          firstVisit: `${this.formatDateTime(entry.firstVisit).datePart} ${
            this.formatDateTime(entry.firstVisit).timePart
          }`,
          lastVisit: `${this.formatDateTime(entry.lastVisit).datePart} ${
            this.formatDateTime(entry.lastVisit).timePart
          }`,
        }));
      }
    },
    tableHeadersComputed() {
      if (!this.combineIPs) {
        return [
          { title: "IP", value: "ip", sortable: true },
          { title: "Mobile/Desktop", value: "device", sortable: true },
          { title: "Location", value: "location", sortable: true },
          { title: "Time", value: "time", sortable: true },
        ];
      } else {
        return [
          { title: "IP", value: "ip", sortable: true },
          { title: "Location", value: "location", sortable: true },
          { title: "Count", value: "count", sortable: true },
          { title: "First Visit", value: "firstVisit", sortable: true },
          { title: "Most Recent Visit", value: "lastVisit", sortable: true },
        ];
      }
    },
  },
  methods: {
    formatDateTime(timestamp) {
      // Day with suffix
      const date = new Date(timestamp);

      const day = date.getDate();
      let suffix = "th";
      if (day % 10 === 1 && day !== 11) suffix = "st";
      else if (day % 10 === 2 && day !== 12) suffix = "nd";
      else if (day % 10 === 3 && day !== 13) suffix = "rd";

      // Month name
      const month = date.toLocaleString("en-US", { month: "long" });
      // Year
      const year = date.getFullYear();
      // Time (12-hour format)
      const timeString = date.toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      // Return them separately
      return {
        datePart: `${month} ${day}${suffix} ${year}`, // e.g. "February 13th 2025"
        timePart: timeString, // e.g. "09:52 AM"
      };
    },
    fetchUsageStats() {
      AdminWS.getUsageStats()
        .then((data) => {
          this.usageStats =
            data.usage_stats.filter((stat) => !stat.video_url) || [];
          this.videoStats =
            data.usage_stats.filter((stat) => stat.video_url) || [];
          this.processStats();
        })
        .catch((error) => {
          console.error("Error fetching usage stats:", error);
        });
    },
    processStats() {
      // Total Clicks Indicator
      this.totalClicks = this.usageStats.length;

      // Process "Clicks Over Time" data (group by day)
      let timeCounts = {};
      this.usageStats.forEach((item) => {
        // Create a date string (YYYY-MM-DD)
        let date = new Date(item.request_time).toISOString().split("T")[0];
        timeCounts[date] = (timeCounts[date] || 0) + 1;
      });
      let timeData = [];
      for (let date in timeCounts) {
        timeData.push({ x: new Date(date).getTime(), y: timeCounts[date] });
      }
      // Sort data by date
      timeData.sort((a, b) => a.x - b.x);
      this.clicksOverTimeSeries = [
        {
          name: "Clicks",
          data: timeData,
        },
      ];

      // Process "User Agent Distribution"
      let mobileCount = 0;
      let desktopCount = 0;
      this.usageStats.forEach((item) => {
        // Simple check: if the user agent contains "Mobi" or "Android", count it as mobile.
        if (/Mobi|Android/i.test(item.user_agent)) {
          mobileCount++;
        } else {
          desktopCount++;
        }
      });
      this.userAgentSeries = [
        {
          name: "User Agents",
          data: [
            { x: "Desktop", y: desktopCount },
            { x: "Mobile", y: mobileCount },
          ],
        },
      ];

      // Process "Clicks by Location" for GeoChart and Pie Chart
      let countryCounts = {};
      this.usageStats.forEach((item) => {
        let geo;
        try {
          geo = JSON.parse(item.geo_location);
        } catch (e) {
          geo = { country: "Unknown" };
        }
        let country = geo.country || "Unknown";
        countryCounts[country] = (countryCounts[country] || 0) + 1;
      });

      const pieLabels = [];
      const pieValues = [];
      const pieColors = [];

      const sortedCountries = Object.entries(countryCounts).sort(
        (a, b) => b[1] - a[1]
      );

      const colorPalette = [
        "#767676",
        "#726f5d",
        "#b7947e",
        "#d9d9d9",
        "#4c3e34",
        "#fefbe7",
      ];

      const countryCategoryMap = {};
      sortedCountries.forEach(([country], i) => {
        // e.g. for i=0 => category=1, i=1 => 2, i=2 => 3, i=3 => 1 again, etc.
        countryCategoryMap[country] = (i % colorPalette.length) + 1;
      });

      // Build GeoChart Data (as before)
      let geoData = [
        ["Country", "Category", { role: "tooltip", type: "string" }],
      ];
      sortedCountries.forEach(([country, count]) => {
        pieLabels.push(country);
        pieValues.push(count);
        const category = countryCategoryMap[country];

        pieColors.push(colorPalette[category - 1]);
        geoData.push([country, countryCategoryMap[country], count + " clicks"]);
      });
      this.geoChartData = geoData;

      this.geoChartOptions = {
        ...this.geoChartOptions,
        colorAxis: {
          values: [1, 2, 3, 4, 5, 6],
          colors: colorPalette,
        },
        datalessRegionColor: "#dee2e6",
      };

      // Build Pie Chart Data for Location Distribution
      this.locationPieSeries = Object.values(countryCounts);
      this.locationPieOptions = {
        chart: { type: "pie" },
        colors: pieColors,
        legend: { show: false },
        labels: Object.keys(countryCounts),
        dataLabels: {
          formatter: function (val, opts) {
            // Return the label (country name) instead of the percentage
            return opts.w.config.labels[opts.seriesIndex];
          },
          style: {
            fontSize: "14px",
            fontFamily: "Copperplate",
            colors: ["#FFF"],
          },
        },
      };
    },
  },
};
</script>

<style scoped></style>
